/*
 * Three Dot Menu Investments
 */

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
// MUI COMPONENTS
import MenuButton from '@mui/icons-material/MoreVert';
import ButtonBase from '@mui/material/ButtonBase';
import PropTypes from 'prop-types';
// BASE
import React, { useState, useCallback, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import Typography from '@mui/material/Typography';
import { a2aTransfersActions, a2aTransfersUtils } from 'app-components/flux/a2aTransfers';
import { actionHelpers } from 'app-components/flux/core';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { tracker } from 'app-components/utils/core';
import { DIALOG_TYPE as DIALOG_CONFIRMATION, mkConfirmationDialogProps } from 'components/Dialogs/ConfirmationDialog';
import {
  DIALOG_TYPE as DIALOG_NOTIFICATION,
  mkNotificationDialogProps,
} from 'components/Dialogs/NotificationDialog/types';
// CUSTOM COMPONENTS
import NestedQMenu from 'components/NestedQMenu';
import { QButtonVariantEnum } from 'components/QButton';
import { Map as ImmutableMap } from 'immutable';
import { useDispatch } from 'react-redux';

const useStyles = makeStyles()((theme) => ({
  menu: {
    marginLeft: 12,
    marginTop: 'auto',
    marginBottom: 'auto',
    width: 32,
    height: 32,
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: theme.palette.background.layer1,
    },
  },
  light: {
    height: 32,
    width: 32,
    padding: 4,
    opacity: 0.5,
    '&:hover': {
      opacity: 0.9,
      color: theme.palette.primary.main,
    },
  },
  errorColor: {
    color: theme.palette.error.main,
  },
  disabledError: {
    color: theme.palette.error.main,
    opacity: 0.5,
  },
  errorWrapper: {
    color: 'inherit',
    fontSize: 'inherit',
  },
  iconSpacing: {
    marginRight: theme.spacing(2),
    marginBottom: 2,
  },
}));

export default function ThreeDotMenuInvestments(props) {
  const { id, transfer, cancelButtonState } = props;
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const [menuAnchor, setMenuAnchor] = useState(null);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const buttonClick = useCallback(
    (e) => {
      e.stopPropagation();
      setMenuAnchor(e.target);
    },
    [setMenuAnchor],
  );

  const onCancelSuccess = useCallback(() => {
    tracker.track(tracker.events.cancelTransfer);
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'cancel-transfer-confirmation-dialog',
          type: DIALOG_NOTIFICATION,
          props: ImmutableMap(
            mkNotificationDialogProps({
              showCloseButton: true,
              size: 'xs',
              title: 'Cancel Transfer Success',
              content: <Typography variant='titleRegular'> Your transfer has been successfully canceled.</Typography>,
            }),
          ),
        }),
      ),
    );
  }, [dispatch]);

  const onCancelFailure = useCallback(() => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'cancel-transfer-failure-dialog',
          type: DIALOG_NOTIFICATION,
          props: ImmutableMap(
            mkNotificationDialogProps({
              showCloseButton: true,
              size: 'xs',
              title: 'Unable to cancel transfer',
              content: (
                <Typography variant='titleRegular' className={classes.errorWrapper}>
                  This transfer is already processing and cannot be canceled.
                </Typography>
              ),
              buttonLabel: 'Got it',
            }),
          ),
        }),
      ),
    );
  }, [classes.errorWrapper, dispatch]);

  const handleConfirmCancel = useCallback(
    (data) => {
      const payload = [data?.id];
      actionHelpers
        .bindPromiseAction(
          dispatch,
          a2aTransfersActions.cancelA2aTransfer,
        )(payload)
        .then(() => {
          onCancelSuccess();
        })
        .catch((_error) => {
          onCancelFailure();
        });
    },
    [dispatch, onCancelFailure, onCancelSuccess],
  );

  const createCancelDialog = useCallback(
    (data) => {
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'cancel-transfer-dialog',
            type: DIALOG_CONFIRMATION,
            props: ImmutableMap(
              mkConfirmationDialogProps({
                showCloseButton: true,
                size: 'xs',
                title: 'Cancel Transfer',
                content: (
                  <Typography variant='titleRegular'>
                    Once you confirm, we will initiate the request to cancel the transfer for you.
                  </Typography>
                ),
                confirmLabel: 'Confirm Cancellation',
                confirmButtonVariant: QButtonVariantEnum.DELETE,
                denyLabel: 'Cancel',
                confirmAction: () => handleConfirmCancel(data),
              }),
            ),
          }),
        ),
      );
    },
    [dispatch, handleConfirmCancel],
  );

  const menuOptions = useMemo(() => {
    const retOptions = [];

    if (cancelButtonState !== 'hidden') {
      const disableCancel = transfer.status !== a2aTransfersUtils.A2A_TRANSFER_ENUMS.SCHEDULED || !transfer?.id;
      retOptions.push({
        disabled: disableCancel,
        customRender: (
          <Typography variant='bodyRegular' className={disableCancel ? classes.disabledError : classes.errorColor}>
            <CloseRoundedIcon className={classes.iconSpacing} />
            Cancel transfer
          </Typography>
        ),
        onSelect: () => {
          if (!disableCancel) {
            handleCloseMenu();
            createCancelDialog(transfer);
          }
        },
        id: 'cancel-transfer',
        tooltip: {
          text: disableCancel ? 'Once a transfer begins processing, it can’t be canceled.' : null,
          placement: 'left',
        },
      });
    }

    return retOptions;
  }, [cancelButtonState, classes, createCancelDialog, handleCloseMenu, transfer]);

  return (
    <>
      <ButtonBase className={classes.menu} onClick={buttonClick} focusRipple id={id}>
        <MenuButton className={classes.light} />
      </ButtonBase>
      <NestedQMenu
        id={'transfer-menu'}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        options={menuOptions}
      />
    </>
  );
}

ThreeDotMenuInvestments.propTypes = {
  transfer: PropTypes.object,
  id: PropTypes.string,
  cancelButtonState: PropTypes.string,
};
